import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  TextField,
  Typography,
  createFilterOptions
} from '@mui/material';
import copy from 'copy-to-clipboard';
import React, { useEffect, useState } from 'react';
import Loader from 'src/components/common/Loader';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { getPoratlHost } from 'src/components/hooks/useUtil';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useEmailActivity } from './useEmailActivity';

export const EmailActivity: React.FunctionComponent<any> = (props: any) => {
  const { openEmailPopoup, setOpenEmailPopoup, documentDetails, permission } = props;
  const MAX_EMAIL_IDS = 1;
  const [isValidEmails, setValidEmails] = useState(true);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [selectedUsersList, setSelectedUsersList] = useState<any>([]);
  const [messageText, setMessageText] = useState('');
  const [anchorElCopied, setAnchorElCopied] = React.useState<HTMLButtonElement | null>(null);
  const openCopied = Boolean(anchorElCopied);
  const dispatch = useAppDispatch();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const user: any = useAppSelector(state => state.user.user);
  const appName = 'ETMF';
  const filter = createFilterOptions<any>();
  const {
    getUsersBasedOnPermission,
    userDataIsLoading,
    sendEmail,
    sendEmailError,
    sendEmailIsError,
    sendEmailIsSuccess,
    sendEmailIsLoading
  } = useEmailActivity();

  const handleCloseCopied = () => {
    setAnchorElCopied(null);
  };

  const handleClickCopied = (event: React.MouseEvent<HTMLButtonElement>) => {
    copy(`${getPoratlHost()}?application=${appName}&key=${documentDetails?.hashed_uri_key}`);
    setAnchorElCopied(event.currentTarget);
    setTimeout(() => {
      handleCloseCopied();
    }, 2000);
  };

  const handleCloseSendDialog = async () => {
    setOpenEmailPopoup(false);
  };

  const handleSendEmail = () => {
    const payload = {
      ui_url: `${getPoratlHost()}?application=${appName}&key=`,
      hashed_uri_key: documentDetails.hashed_uri_key, // document hashed url key
      user_name: `${`${user.firstName} ${user.lastName}`}`,
      subject: documentDetails.document_data.tmf_document_name, // document name
      body: messageText,
      module: 'Send Link',
      sent_by: user.email,
      recipients: selectedUsersList.map((obj: any) => {
        return {
          user_id: obj?.id,
          user_name: obj?.user_name,
          recipient: obj?.email
        };
      })
    };

    sendEmail({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      data: payload,
      application: process.env.REACT_APP_STARTUP_NAME,
      module_section_related: 'STUDY LOGS',
      parent_entity_related: documentDetails.document_data.tmf_document_name
    });
  };

  const getUsers = async () => {
    const temp: any = await getUsersBasedOnPermission({
      client_name: 'ctms-ui',
      data: [`${permission}`]
    });
    const fileredUser = temp?.data
      .filter((ele: any) => ele.email !== null)
      .map((ele: any) => {
        return {
          id: ele.id,
          email: ele.email,
          first_name: ele.first_name,
          last_name: ele.last_name,
          user_name: `${ele.first_name} ${ele.last_name}`
        };
      });
    if (fileredUser.length > 0) {
      setUsersList(fileredUser);
    } else {
      setUsersList([]);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    if (userDataIsLoading || sendEmailIsLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [userDataIsLoading, sendEmailIsLoading]);

  useEffect(() => {
    if (sendEmailIsSuccess) {
      dispatch(
        setMessage({
          msgDetails: 'E-Mail sent successfully.',
          severity: 'success'
        })
      );
      setOpenEmailPopoup(false);
    }
    if (sendEmailIsError) {
      const err: any = sendEmailError;
      if (err?.status == 500 || err?.status == 'FETCH_ERROR') {
        dispatch(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
  }, [sendEmailIsSuccess, sendEmailIsError]);

  return (
    <div>
      <PopupComponent open={openEmailPopoup} maxWidth="sm">
        <Loader />
        <DialogTitle>Share</DialogTitle>
        <DialogContent className="share-dialog">
          <Autocomplete
            multiple
            id="tags-standard"
            options={usersList}
            getOptionLabel={(option: any) =>
              `${option?.first_name ?? ``} ${option?.last_name ?? ``} (${option?.email})`
            }
            freeSolo
            value={selectedUsersList}
            clearOnBlur
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              const isExisting = options.some(option => inputValue === option?.email);
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  email: `Add ${inputValue}`
                });
              }
              return filtered;
            }}
            renderTags={(value: readonly any[], getTagProps) =>
              value.map((option: any, index: number) => (
                <div key={index}>
                  <Chip variant="outlined" label={option?.email || option} {...getTagProps({ index })} />
                </div>
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                label="Enter Name or Email"
                placeholder="Name/Email of person"
              />
            )}
            onChange={(event, newValue) => {
              const regex = new RegExp('[a-z0-9].[a-z0-9]+@[a-z]+.[a-z]{2,3}');
              let isValidEmail = true;
              const userTemp: any = [];
              newValue?.forEach(value => {
                if (typeof value === 'string') {
                  if (regex.test(value)) {
                    userTemp.push({ email: value });
                  }
                } else if (value.inputValue) {
                  if (regex.test(value.inputValue)) {
                    userTemp.push({ email: value.inputValue });
                  }
                } else {
                  const email = value?.email || value;
                  userTemp.push({
                    email: value.email,
                    id: value.id,
                    first_name: value.first_name,
                    last_name: value.last_name,
                    user_name: `${value.first_name ?? ``} ${value.last_name ?? ``}`
                  });
                  if (!regex.test(email)) {
                    isValidEmail = false;
                    return;
                  }
                }
              });
              setValidEmails(isValidEmail);
              if (isValidEmail) {
                setSelectedUsersList(userTemp);
              }
            }}
          />
          {!isValidEmails && <Alert severity="error">Entered mail ID is not valid.</Alert>}
          <TextField
            id="standard-multiline-static"
            multiline
            margin="normal"
            minRows={2}
            maxRows={5}
            placeholder="Type your message here..."
            fullWidth
            variant="standard"
            sx={{ mt: 5 }}
            onChange={e => {
              e.target.value = e.target.value?.trimStart();
              setMessageText(e.target.value);
            }}
            inputProps={{
              maxLength: 300
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', mt: 3, pl: 2, pr: 2 }}>
          <div>
            <Button onClick={handleClickCopied} startIcon={<LinkIcon />} sx={{ ml: 0.5 }} className="nouppercase-btn">
              Copy Shareable Link
            </Button>

            <Popover
              open={openCopied}
              anchorEl={anchorElCopied}
              onClose={handleCloseCopied}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              className="copiedPop"
            >
              <Typography sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon /> &nbsp; Link Copied to Clipboard
              </Typography>
            </Popover>
          </div>
          <div>
            <Button onClick={handleCloseSendDialog} className="grey-btn">
              Close
            </Button>
            <Button onClick={handleSendEmail} disabled={selectedUsersList?.length < MAX_EMAIL_IDS}>
              Share
            </Button>
          </div>
        </DialogActions>
      </PopupComponent>
    </div>
  );
};
